import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Bell, X, Check, AlertCircle, CheckCircle2, RotateCcw } from 'lucide-react';
import { describeChangeHeadline, describeChangeDetails, describeAssetRef } from '../utils/changeLabels';
import { PushSettingsButton } from './PushNotifications';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtRelative = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = Math.round((new Date() - d) / 60000);
  if (diff < 1) return 'ahora';
  if (diff < 60) return `hace ${diff}m`;
  if (diff < 1440) return `hace ${Math.floor(diff / 60)}h`;
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
};

const NotificationBell = () => {
  const [tab, setTab] = useState('cambios');           // 'cambios' | 'notifs'
  const [open, setOpen] = useState(false);

  // Cambios (nuevo flujo)
  const [changes, setChanges] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Notifs (sistema previo)
  const [notifs, setNotifs] = useState([]);

  const fetchAll = useCallback(async () => {
    try {
      const [chRes, nRes] = await Promise.all([
        axios.get(`${API}/me/changes-since-last-session?limit=100`, { withCredentials: true }).catch(() => ({ data: { items: [] } })),
        axios.get(`${API}/notifications`, { withCredentials: true }).catch(() => ({ data: { items: [] } })),
      ]);
      setChanges(chRes.data?.items ?? []);
      setNotifs(nRes.data?.items ?? nRes.data ?? []);
      window.dispatchEvent(new CustomEvent('xenicos:notifications-refreshed'));
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    fetchAll();
    const i = setInterval(fetchAll, 30000);
    // Allow other components (ChangesPopup, etc.) to ask the bell to refresh.
    const onRefresh = () => fetchAll();
    window.addEventListener('xenicos:refresh-bell', onRefresh);
    return () => {
      clearInterval(i);
      window.removeEventListener('xenicos:refresh-bell', onRefresh);
    };
  }, [fetchAll]);

  const unreadNotifs = useMemo(() => notifs.filter(n => !n.read).length, [notifs]);
  const changesCount = changes.length;
  const totalBadge = unreadNotifs + changesCount;

  // ----- Actions on "cambios" -----
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelectedIds(new Set(changes.map(c => c.id).filter(Boolean)));
  const selectNone = () => setSelectedIds(new Set());

  const markChanges = async (status) => {
    const ids = Array.from(selectedIds).filter(Boolean);
    if (ids.length === 0) return;
    await axios.post(`${API}/me/changes/mark`, { ids, status }, { withCredentials: true });
    setSelectedIds(new Set());
    fetchAll();
    try { window.dispatchEvent(new CustomEvent('xenicos:refresh-pendientes')); } catch {}
  };

  const markOne = async (id, status) => {
    if (!id) return;
    await axios.post(`${API}/me/changes/mark`, { ids: [id], status }, { withCredentials: true });
    setSelectedIds(prev => { const n = new Set(prev); n.delete(id); return n; });
    fetchAll();
    try { window.dispatchEvent(new CustomEvent('xenicos:refresh-pendientes')); } catch {}
  };

  // ----- Actions on "notifs" -----
  const markNotifRead = async (id) => {
    await axios.put(`${API}/notifications/${id}/read`, {}, { withCredentials: true });
    setNotifs(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  };
  const markAllNotifsRead = async () => {
    await axios.put(`${API}/notifications/read-all`, {}, { withCredentials: true });
    setNotifs(prev => prev.map(n => ({ ...n, read: true })));
  };

  // Open the bell on the relevant tab (cambios if any, else notifs).
  const handleOpen = () => {
    if (!open) {
      setTab(changesCount > 0 ? 'cambios' : (unreadNotifs > 0 ? 'notifs' : 'cambios'));
    }
    setOpen(o => !o);
  };

  return (
    <div className="relative" data-testid="notification-bell">
      <button
        onClick={handleOpen}
        className="relative p-2 text-slate-500 hover:text-slate-700 transition-colors"
        data-testid="bell-btn"
        aria-label="Notificaciones"
      >
        <Bell className="w-5 h-5" />
        {totalBadge > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center min-w-[18px] h-[18px] px-1"
            data-testid="notif-count"
          >
            {totalBadge > 99 ? '99+' : totalBadge}
          </span>
        )}
      </button>

      {open && (
        <div
          className="fixed inset-x-2 top-16 w-auto max-w-none sm:absolute sm:inset-x-auto sm:top-full sm:right-0 sm:mt-2 sm:w-[420px] sm:max-w-[92vw] bg-white rounded-xl shadow-2xl border border-slate-200 z-50 max-h-[75vh] flex flex-col"
          data-testid="notif-dropdown"
        >
          {/* Tabs */}
          <div className="flex border-b border-slate-200">
            <button
              onClick={() => setTab('cambios')}
              className={`flex-1 px-3 py-2.5 text-sm font-medium transition-colors ${
                tab === 'cambios' ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/40' : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid="bell-tab-cambios"
            >
              Cambios
              {changesCount > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-100 text-red-700 text-[10px] font-bold">
                  {changesCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setTab('notifs')}
              className={`flex-1 px-3 py-2.5 text-sm font-medium transition-colors ${
                tab === 'notifs' ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/40' : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid="bell-tab-notifs"
            >
              Notificaciones
              {unreadNotifs > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-100 text-red-700 text-[10px] font-bold">
                  {unreadNotifs}
                </span>
              )}
            </button>
            <button
              onClick={() => setOpen(false)}
              className="px-3 text-slate-400 hover:text-slate-600"
              aria-label="Cerrar"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* ===== CAMBIOS TAB ===== */}
          {tab === 'cambios' && (
            <>
              {/* Bulk action bar (only when there are items) */}
              {changes.length > 0 && (
                <div className="px-3 py-2 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between gap-2 text-xs">
                  <div className="flex items-center gap-1.5">
                    <span className="text-slate-600">
                      {selectedIds.size === 0
                        ? `${changes.length} cambio${changes.length === 1 ? '' : 's'}`
                        : `${selectedIds.size} seleccionado${selectedIds.size === 1 ? '' : 's'}`}
                    </span>
                    {selectedIds.size === 0 ? (
                      <button onClick={selectAll} className="text-blue-600 hover:underline" data-testid="changes-select-all">Seleccionar todos</button>
                    ) : (
                      <button onClick={selectNone} className="text-slate-500 hover:underline" data-testid="changes-select-none">Limpiar</button>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => markChanges('ok')}
                      disabled={selectedIds.size === 0}
                      className="flex items-center gap-1 px-2 py-1 rounded text-emerald-700 bg-emerald-50 hover:bg-emerald-100 disabled:opacity-40 disabled:cursor-not-allowed font-medium"
                      data-testid="changes-bulk-ok"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      OK
                    </button>
                    <button
                      onClick={() => markChanges('revisar')}
                      disabled={selectedIds.size === 0}
                      className="flex items-center gap-1 px-2 py-1 rounded text-amber-700 bg-amber-50 hover:bg-amber-100 disabled:opacity-40 disabled:cursor-not-allowed font-medium"
                      data-testid="changes-bulk-revisar"
                    >
                      <AlertCircle className="w-3.5 h-3.5" />
                      Revisar
                    </button>
                  </div>
                </div>
              )}

              <div className="overflow-y-auto flex-1">
                {changes.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-10">Sin cambios pendientes</p>
                ) : (
                  changes.map((c) => {
                    const id = c.id;
                    const checked = selectedIds.has(id);
                    const label = describeChangeHeadline(c);
                    const details = describeChangeDetails(c);
                    const assetRef = describeAssetRef(c);
                    const isRevisar = c.review_status === 'revisar';
                    return (
                      <div
                        key={id || c.created_at}
                        className={`p-3 border-b border-slate-50 hover:bg-slate-50 ${isRevisar ? 'bg-amber-50/40' : ''}`}
                        data-testid={`change-${id}`}
                      >
                        <div className="flex items-start gap-2">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleSelect(id)}
                            className="mt-1 flex-shrink-0 cursor-pointer"
                            data-testid={`change-check-${id}`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <p className="text-sm text-slate-800 truncate">
                                <span className="font-medium">{label}</span>
                                {c.user_name && <span className="text-slate-500"> · {c.user_name}</span>}
                              </p>
                              <span className="text-[10px] text-slate-400 whitespace-nowrap flex-shrink-0">{fmtRelative(c.created_at)}</span>
                            </div>
                            {details.length > 0 && (
                              <ul className="mt-1 space-y-0.5 text-xs text-slate-600">
                                {details.map((d, i) => (
                                  <li key={i} className="leading-snug">{d}</li>
                                ))}
                              </ul>
                            )}
                            <div className="flex items-center gap-2 mt-1">
                              {c.asset_id && (
                                <Link
                                  to={`/activos/${c.asset_id}`}
                                  onClick={() => setOpen(false)}
                                  className="text-xs text-blue-600 hover:underline truncate"
                                  data-testid={`change-link-${id}`}
                                  title={assetRef}
                                >
                                  {assetRef || `activo ${c.asset_id.slice(0, 8)}…`}
                                </Link>
                              )}
                              {isRevisar && (
                                <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                                  <AlertCircle className="w-3 h-3" /> Revisar
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1.5 mt-1.5">
                              <button
                                onClick={() => markOne(id, 'ok')}
                                className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] text-emerald-700 hover:bg-emerald-100"
                                data-testid={`change-ok-${id}`}
                              >
                                <Check className="w-3 h-3" /> OK
                              </button>
                              {!isRevisar ? (
                                <button
                                  onClick={() => markOne(id, 'revisar')}
                                  className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] text-amber-700 hover:bg-amber-100"
                                  data-testid={`change-revisar-${id}`}
                                >
                                  <AlertCircle className="w-3 h-3" /> Revisar
                                </button>
                              ) : (
                                <button
                                  onClick={() => markOne(id, 'pending')}
                                  className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] text-slate-600 hover:bg-slate-100"
                                  data-testid={`change-undo-${id}`}
                                >
                                  <RotateCcw className="w-3 h-3" /> Deshacer
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </>
          )}

          {/* ===== NOTIFICACIONES TAB (sistema previo) ===== */}
          {tab === 'notifs' && (
            <>
              {unreadNotifs > 0 && (
                <div className="px-3 py-2 border-b border-slate-100 bg-slate-50/50 flex items-center justify-end">
                  <button
                    onClick={markAllNotifsRead}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                    data-testid="mark-all-read"
                  >
                    Marcar todo leído
                  </button>
                </div>
              )}
              <div className="overflow-y-auto flex-1">
                {notifs.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-10">Sin notificaciones</p>
                ) : (
                  notifs.map(n => {
                    // (Feb 2026) Etiqueta compacta del activo — misma
                    // convención que la pestaña "Cambios": "DIRECCIÓN,
                    // Municipio · NPL-XXXX". Fallback al asset_id truncado
                    // si no llega el enrich (activo huérfano / borrado).
                    const assetLabelParts = [];
                    if (n.direccion) assetLabelParts.push(n.direccion);
                    if (n.municipio) assetLabelParts.push(n.municipio);
                    let assetLabel = assetLabelParts.join(', ');
                    if (n.codigo_npl) assetLabel = assetLabel ? `${assetLabel} · ${n.codigo_npl}` : n.codigo_npl;
                    if (!assetLabel && n.asset_id) assetLabel = `activo ${n.asset_id.slice(0, 8)}…`;
                    return (
                      <div
                        key={n.id}
                        className={`p-3 border-b border-slate-50 hover:bg-slate-50 ${!n.read ? 'bg-blue-50/50' : ''}`}
                        data-testid={`notif-${n.id}`}
                      >
                        <div className="flex items-start gap-2">
                          {!n.read && <div className="w-2 h-2 bg-blue-500 rounded-full mt-1.5 flex-shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm ${!n.read ? 'font-medium text-slate-900' : 'text-slate-600'}`}>{n.titulo}</p>
                            <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.mensaje}</p>
                            {n.asset_id && (
                              <Link
                                to={`/activos/${n.asset_id}`}
                                onClick={() => { if (!n.read) markNotifRead(n.id); setOpen(false); }}
                                className="inline-block mt-1 text-xs text-blue-600 hover:underline truncate max-w-full"
                                data-testid={`notif-asset-link-${n.id}`}
                                title={assetLabel}
                              >
                                {assetLabel}
                              </Link>
                            )}
                            <div className="flex items-center justify-between gap-2 mt-1">
                              <p className="text-[10px] text-slate-400">{fmtRelative(n.created_at)}</p>
                              {!n.read && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); markNotifRead(n.id); }}
                                  className="text-[11px] text-blue-600 hover:text-blue-800 font-medium"
                                  data-testid={`notif-mark-read-${n.id}`}
                                >
                                  Marcar leída
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </>
          )}
          <PushSettingsButton onOpen={() => setOpen(false)} />
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
